
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FloatingButtons } from "@/components/common/FloatingButtons";
import { Award, Users, TrendingUp, Heart, Target, Eye, Zap, Calendar } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { sendEmail, type FormData as EmailFormData } from "@/utils/email";

interface ScheduleMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ScheduleMeetingDialog = ({ open, onOpenChange }: ScheduleMeetingDialogProps) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [purpose, setPurpose] = useState('loan-consultation');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    const message = `New Meeting Request from Website:

👤 Name: ${name}
📧 Email: ${email}
📞 Phone: ${phone}
📅 Date: ${date}
⏰ Time: ${time}
🎯 Purpose: ${purpose}

---
📅 Submitted on: ${new Date().toLocaleString()}
🌐 This is an automated message from the website contact form.`;

    // Submit to WhatsApp
    window.open(`https://wa.me/919324471736?text=${encodeURIComponent(message)}`, '_blank');
    
    // Prepare email data with proper typing
    const emailData: EmailFormData & Record<string, unknown> = {
      from_name: name,
      from_email: email || 'noreply@whitepearlfinance.com',
      to_email: import.meta.env.VITE_EMAILJS_BUSINESS_EMAIL || 'whitepearlfinance@gmail.com',
      phone: phone,
      loan_type: purpose,
      message: message,
      // Additional fields used by the email template
      date: new Date().toLocaleDateString(),
      time: new Date().toLocaleTimeString(),
      company_name: 'White Pearl Finance',
      subject: `New Meeting Request from ${name}`,
      to_name: 'Support Team',
      whatsapp_message: message.replace(/\n/g, '<br>')
    };

    try {
      // Submit to Email using the existing email utility
      const { success, message: emailMessage } = await sendEmail(emailData);
      
      if (success) {
        // Reset form on success
        setDate('');
        setTime('');
        setName('');
        setEmail('');
        setPhone('');
        setPurpose('Loan Consultation');
        
        // Show success message
        toast({
          title: "Meeting Request Sent!",
          description: "Our team will confirm your meeting shortly. We've also sent a confirmation to your email.",
        });
      } else {
        throw new Error(emailMessage || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      // Even if email fails, we still show success since WhatsApp submission worked
      toast({
        title: "Meeting Request Sent!",
        description: "Our team will confirm your meeting shortly. Note: Email confirmation was not sent.",
      });
    } finally {
      // Always close the modal after submission
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center mb-2">Schedule a Meeting</DialogTitle>
          <p className="text-sm text-gray-400 text-center mb-6">
            Book a 30-minute consultation with our loan expert
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-white">Your Name *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Date *</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Time *</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
                min="09:00"
                max="19:00"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-white">Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Phone Number *</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white"
              required
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white">Purpose *</Label>
            <Select
              value={purpose}
              onValueChange={setPurpose}
              required
            >
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Select purpose" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600 text-white">
                <SelectItem value="Loan Consultation">Loan Consultation</SelectItem>
                <SelectItem value="Document Submission">Document Submission</SelectItem>
                <SelectItem value="Follow-up Meeting">Follow-up Meeting</SelectItem>
                <SelectItem value="Loan Application">Loan Application</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 mt-4">
            Schedule Meeting
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const About = () => {
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const achievements = [
    {
      icon: Users,
      number: "150+",
      label: "Happy Customers",
      description: "Served across Mumbai"
    },
    {
      icon: TrendingUp,
      number: "₹100 Cr+",
      label: "Loans Disbursed",
      description: "In the last 3 years"
    },
    {
      icon: Award,
      number: "98%",
      label: "Approval Rate",
      description: "Industry leading"
    },
    {
      icon: Zap,
      number: "48 Hours",
      label: "Average Approval",
      description: "Lightning fast"
    }
  ];

  const team = [
    {
      name: "Mittal Sampat",
      position: "CEO & Founder",
      experience: "15+ Years in Banking & Finance",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      description: "Former VP at HDFC Bank with expertise in retail lending and financial services",
      avatar: "MS"
    },
    {
      name: "Rahul Upadhyay",
      position: "Chief Operating Officer",
      experience: "12+ Years in Financial Operations",
      image: "https://images.unsplash.com/photo-1494790108755-2616b9da0b7c?w=300&h=300&fit=crop&crop=face",
      description: "Operations leader with expertise in process optimization and customer experience",
      avatar: "RU"
    }
  ];

  // Certifications array removed as the section is hidden

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About White Pearl Finance
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            We're on a mission to democratize financial services and make loans accessible, transparent, and affordable for everyone.
          </p>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            <Card className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 backdrop-blur-lg border-white/20">
              <CardContent className="p-8 text-center">
                <Target className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Our Mission</h3>
                <p className="text-gray-300 leading-relaxed">
                  To provide instant, transparent, and affordable financial solutions that empower individuals and businesses to achieve their dreams and grow their aspirations.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-teal-500/20 backdrop-blur-lg border-white/20">
              <CardContent className="p-8 text-center">
                <Eye className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Our Vision</h3>
                <p className="text-gray-300 leading-relaxed">
                  To become India's most trusted digital lending platform, known for our customer-first approach, innovation, and commitment to financial inclusion.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500/20 to-red-500/20 backdrop-blur-lg border-white/20">
              <CardContent className="p-8 text-center">
                <Heart className="h-12 w-12 text-orange-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-4">Our Values</h3>
                <p className="text-gray-300 leading-relaxed">
                  Transparency, Trust, Technology, and Customer Success. We believe in ethical lending practices and building long-term relationships with our customers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">Our Achievements</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20 text-center hover:scale-105 transition-all duration-300">
                <CardContent className="p-8">
                  <achievement.icon className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-white mb-2">{achievement.number}</div>
                  <div className="text-xl text-blue-400 font-semibold mb-2">{achievement.label}</div>
                  <div className="text-gray-300 text-sm">{achievement.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white/10 backdrop-blur-lg border-white/20">
              <CardContent className="p-12">
                <h2 className="text-4xl font-bold text-white text-center mb-8">Our Story</h2>

                <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                  <p>
                    White Pearl Finance was founded in 2021 with a simple yet powerful vision: to make financial services accessible to everyone. Our founders, with over 50 years of combined experience in banking and technology, witnessed firsthand the challenges people faced in accessing quick, transparent, and affordable loans.
                  </p>

                  <p>
                    Traditional lending processes were slow, opaque, and often discriminatory. We decided to change that by leveraging technology, data analytics, and a customer-first approach to create a revolutionary lending platform.
                  </p>

                  <p>
                    Today, we've helped over 150 customers realize their dreams - from buying their first home to expanding their businesses. Our AI-powered platform processes loan applications in minutes, not days, while maintaining the highest standards of security and compliance.
                  </p>

                  <p>
                    We're not just a lending company; we're your financial partners committed to your success. Every loan we disburse is a step towards someone's dream, and that drives us every single day.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">Leadership Team</h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-lg border-white/20 hover:bg-white/15 transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="relative mb-6">
                    <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto flex items-center justify-center text-white text-2xl font-bold">
                      {member.avatar}
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-white mb-1">{member.name}</h3>
                  <p className="text-blue-400 font-medium mb-2">{member.position}</p>
                  <p className="text-gray-400 text-sm mb-3">{member.experience}</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications - Hidden as per request
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-4xl font-bold text-white text-center mb-12">Certifications & Compliance</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <Card key={index} className="bg-gradient-to-br from-green-500/20 to-blue-500/20 backdrop-blur-lg border-white/20">
                <CardContent className="p-6 text-center">
                  <cert.icon className="h-10 w-10 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{cert.title}</h3>
                  <p className="text-gray-300 text-sm">{cert.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Contact Information */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <Card className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg border-white/20 max-w-4xl mx-auto">
            <CardContent className="p-12">
              <h2 className="text-4xl font-bold text-white text-center mb-8">Get in Touch</h2>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-semibold text-white mb-4">Visit Our Office</h3>
                  <div className="space-y-3 text-gray-300">
                    <p>
                      <strong>Address:</strong><br />
                      10, Ganjawala Apt,<br />
                      Building No. 3,<br />
                      Borivali (W),<br />
                      Mumbai - 400092
                    </p>
                    <p>
                      <strong>Business Hours:</strong><br />
                      Monday - Saturday: 9:00 AM - 7:00 PM<br />

                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-white mb-4">Contact Details</h3>
                  <div className="space-y-3 text-gray-300">
                    <p>
                      <strong>Phone:</strong> <a href="tel:+919324471736" className="text-blue-400 hover:underline">+91 93244 71736</a>
                    </p>
                    <p>
                      <strong>WhatsApp:</strong> <a href="https://wa.me/919324471736" className="text-green-400 hover:underline">+91 93244 71736</a>
                    </p>
                    <p>
                      <strong>Email:</strong> <a href="mailto:info@whitepearlfinance.com" className="text-blue-400 hover:underline">info@whitepearlfinance.com</a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="text-center mt-8">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-lg px-8 py-4 h-auto"
                  onClick={() => setIsMeetingDialogOpen(true)}
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule a Meeting
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <FloatingButtons />

      <ScheduleMeetingDialog
        open={isMeetingDialogOpen}
        onOpenChange={setIsMeetingDialogOpen}
      />
    </div>
  );
};

export default About;
